import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  deleteApplication,
  getAllApplications,
  getApplication,
  updateApplication,
  type ApplicationDetail,
  type ListApplicationsParams,
  type PaginatedApplications,
  type UpdateApplicationBody,
} from "../api/applications";
import { deleteCad3dUpload } from "../api/cad3d";

/**
 * Admin all-applications list (§L — HTTP via TanStack Query). `keepPreviousData`
 * avoids a flash while refetching.
 */
export function useAllApplications(params: ListApplicationsParams) {
  return useQuery<PaginatedApplications>({
    queryKey: ["applications", "all", params],
    queryFn: () => getAllApplications(params),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

/**
 * Infinite all-applications list for dropdowns — paginates `/v1/applications/all`
 * by `offset`, accumulating pages so the menu can load more on scroll-to-bottom.
 */
export function useInfiniteAllApplications(
  params: Omit<ListApplicationsParams, "offset"> = {},
) {
  const limit = params.limit ?? 20;
  return useInfiniteQuery<PaginatedApplications>({
    queryKey: ["applications", "all", "infinite", { ...params, limit }],
    queryFn: ({ pageParam }) =>
      getAllApplications({ ...params, limit, offset: pageParam as number }),
    initialPageParam: 0,
    getNextPageParam: (last) => {
      const next = last.offset + last.items.length;
      return next < last.total ? next : undefined;
    },
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

/** Full application + fastener lines for the edit/view modal. */
export function useApplication(id: string | null) {
  return useQuery<ApplicationDetail>({
    queryKey: ["applications", "detail", id],
    queryFn: () => getApplication(id as string),
    enabled: !!id,
    staleTime: 30_000,
  });
}

/** PATCH /v1/applications/:id; refreshes the list + the edited detail. */
export function useUpdateApplication() {
  const queryClient = useQueryClient();
  return useMutation<ApplicationDetail, Error, { id: string; body: UpdateApplicationBody }>({
    mutationFn: ({ id, body }) => updateApplication(id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["applications"] });
    },
  });
}

/** DELETE /v1/applications/:id (soft delete); refreshes the list. */
export function useDeleteApplication() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => deleteApplication(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["applications"] });
    },
  });
}

/**
 * DELETE the current 3D STEP upload for an application (svc-09). Refreshes the
 * applications list so the cleared cad3dFileName/status are reflected. Allowed
 * regardless of analysis status (so a stuck/worker-down "queued" upload can be
 * removed).
 */
export function useDeleteCad3dUpload() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (applicationId) => deleteCad3dUpload(applicationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["applications"] });
    },
  });
}
